import React, { useState } from 'react';
import styles from './FlightDetailsStep.module.scss';
import type { FlightCardPayload, FlightBaggageOption } from './FlightCardWidget';
import { Slider } from './Slider';
import {
    AirlineLogo,
    formatDuration,
    formatMoney,
    formatTime24,
    formatDateShort,
    airlineName,
    isDifferentDay as isDifferentAirportDay,
} from './flightPresentation';

/* Terminal-facing short names, paired with the IATA code. */
const AIRPORT_SHORT: Record<string, string> = {
    LHR: 'Heathrow',
    LGW: 'Gatwick',
    STN: 'Stansted',
    SFO: 'San Francisco',
    JFK: 'John F. Kennedy',
    EWR: 'Newark',
    LAX: 'Los Angeles',
    CDG: 'Charles de Gaulle',
    AMS: 'Schiphol',
    FRA: 'Frankfurt',
    DXB: 'Dubai',
    SIN: 'Changi',
    HND: 'Haneda',
    NRT: 'Narita',
    ORD: "O'Hare",
    ATL: 'Hartsfield-Jackson',
    MIA: 'Miami',
    BOS: 'Logan',
    SEA: 'Seattle-Tacoma',
    DEN: 'Denver',
    SOF: 'Sofia',
};

// City from IATA (simple fallback)
const AIRPORT_CITIES: Record<string, string> = {
    SOF: 'Sofia',
    LHR: 'London',
    SFO: 'San Francisco',
    JFK: 'New York',
    LAX: 'Los Angeles',
    CDG: 'Paris',
    AMS: 'Amsterdam',
    FRA: 'Frankfurt',
    DXB: 'Dubai',
    SIN: 'Singapore',
    HND: 'Tokyo',
    NRT: 'Tokyo',
    ORD: 'Chicago',
    ATL: 'Atlanta',
    MIA: 'Miami',
    BOS: 'Boston',
    SEA: 'Seattle',
    DEN: 'Denver',
    EWR: 'New York',
    LGW: 'London',
    STN: 'London',
};

/* The middle URL in the previous set 404s, which rendered a broken-image
   box on the card. These three are verified, and the img hides itself if
   one ever goes away rather than showing the browser's placeholder. */
const FARE_IMAGES = [
    'https://images.unsplash.com/photo-1540339832862-474599807836?w=400&h=240&fit=crop&q=80',
    'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=240&fit=crop&q=80',
    'https://images.unsplash.com/photo-1581553680321-4fffae59fccd?w=400&h=240&fit=crop&q=80',
];

/* Explicit rather than inferred from FARE_TIERS below -- buildApiFareTiers
   needs to return this same shape from real gateway data. */
type FareTierFeature = {
    included: boolean;
    label: string;
    detail?: string;
    info?: string;
};

type FareTier = {
    name: string;
    priceAdd: number;
    features: FareTierFeature[];
};

const FARE_TIERS: FareTier[] = [
    {
        name: 'Basic fare',
        priceAdd: 0,
        features: [
            { included: true, label: 'Small personal item — 1 pc', detail: '40x15x30 cm', info: 'One item that fits under the seat in front of you.' },
            { included: false, label: 'No carry-on bag' },
            { included: false, label: 'No checked bag' },
            { included: false, label: 'No changes' },
            { included: false, label: 'No refunds', info: 'The fare is not refundable if you cancel.' },
        ],
    },
    {
        name: 'With checked bag',
        priceAdd: 100,
        features: [
            { included: true, label: 'Small personal item — 1 pc', detail: '40x15x30 cm', info: 'One item that fits under the seat in front of you.' },
            { included: false, label: 'No carry-on bag' },
            { included: true, label: 'Checked bag — 1 pc' },
            { included: false, label: 'No changes' },
            { included: false, label: 'No refunds', info: 'The fare is not refundable if you cancel.' },
        ],
    },
    {
        name: 'With checked bag and changes',
        priceAdd: 200,
        features: [
            { included: true, label: 'Small personal item — 1 pc', detail: '40x15x30 cm', info: 'One item that fits under the seat in front of you.' },
            { included: false, label: 'No carry-on bag' },
            { included: true, label: 'Checked bag 23kg — 1 pc' },
            { included: true, label: 'Changes allowed', info: 'Change the date or time; a fare difference may apply.' },
            { included: false, label: 'No refunds', info: 'The fare is not refundable if you cancel.' },
        ],
    },
    /* Test data for the drag slider — a real gateway response can return
       more than 3 tiers, so these two push the row past the 3-visible
       width and give it something to actually scroll. */
    {
        name: 'Flex fare',
        priceAdd: 350,
        features: [
            { included: true, label: 'Small personal item — 1 pc', detail: '40x15x30 cm', info: 'One item that fits under the seat in front of you.' },
            { included: true, label: 'Carry-on bag — 1 pc', detail: '55x40x20 cm' },
            { included: true, label: 'Checked bag 23kg — 1 pc' },
            { included: true, label: 'Changes allowed', info: 'Change the date or time; a fare difference may apply.' },
            { included: true, label: 'Refundable', info: 'Cancel any time before departure for a full refund.' },
        ],
    },
    {
        name: 'Premium fare',
        priceAdd: 520,
        features: [
            { included: true, label: 'Small personal item — 1 pc', detail: '40x15x30 cm', info: 'One item that fits under the seat in front of you.' },
            { included: true, label: 'Carry-on bag — 1 pc', detail: '55x40x20 cm' },
            { included: true, label: 'Checked bags 32kg — 2 pc' },
            { included: true, label: 'Changes allowed', info: 'Change the date or time; a fare difference may apply.' },
            { included: true, label: 'Refundable', info: 'Cancel any time before departure for a full refund.' },
            { included: true, label: 'Priority boarding & extra legroom' },
        ],
    },
];

/* Test data for a round trip's return leg -- its own fare tiers, priced and
   featured independently of the outbound ones, so the two rows genuinely
   behave as separate selections rather than mirroring each other. */
const RETURN_FARE_TIERS: FareTier[] = [
    {
        name: 'Basic fare',
        priceAdd: 0,
        features: [
            { included: true, label: 'Small personal item — 1 pc', detail: '40x15x30 cm', info: 'One item that fits under the seat in front of you.' },
            { included: false, label: 'No carry-on bag' },
            { included: false, label: 'No checked bag' },
            { included: false, label: 'No changes' },
            { included: false, label: 'No refunds', info: 'The fare is not refundable if you cancel.' },
        ],
    },
    {
        name: 'With checked bag',
        priceAdd: 90,
        features: [
            { included: true, label: 'Small personal item — 1 pc', detail: '40x15x30 cm', info: 'One item that fits under the seat in front of you.' },
            { included: false, label: 'No carry-on bag' },
            { included: true, label: 'Checked bag — 1 pc' },
            { included: false, label: 'No changes' },
            { included: false, label: 'No refunds', info: 'The fare is not refundable if you cancel.' },
        ],
    },
    {
        name: 'With checked bag and changes',
        priceAdd: 180,
        features: [
            { included: true, label: 'Small personal item — 1 pc', detail: '40x15x30 cm', info: 'One item that fits under the seat in front of you.' },
            { included: false, label: 'No carry-on bag' },
            { included: true, label: 'Checked bag 23kg — 1 pc' },
            { included: true, label: 'Changes allowed', info: 'Change the date or time; a fare difference may apply.' },
            { included: false, label: 'No refunds', info: 'The fare is not refundable if you cancel.' },
        ],
    },
    {
        name: 'Flex fare',
        priceAdd: 300,
        features: [
            { included: true, label: 'Small personal item — 1 pc', detail: '40x15x30 cm', info: 'One item that fits under the seat in front of you.' },
            { included: true, label: 'Carry-on bag — 1 pc', detail: '55x40x20 cm' },
            { included: true, label: 'Checked bag 23kg — 1 pc' },
            { included: true, label: 'Changes allowed', info: 'Change the date or time; a fare difference may apply.' },
            { included: true, label: 'Refundable', info: 'Cancel any time before departure for a full refund.' },
        ],
    },
];

/* Small personal item is a Basic-Economy universal -- every real airline
   fare includes it, and the payload has no field for it either way, so it
   stays a flat inclusion rather than something derived from baggage_options. */
const PERSONAL_ITEM_FEATURE: FareTierFeature = {
    included: true,
    label: 'Small personal item — 1 pc',
    detail: '40x15x30 cm',
    info: 'One item that fits under the seat in front of you.',
};

/**
 * Builds this leg's fare-tier cards from the gateway's own
 * baggage_options[]/conditions_options[] (flights-details.yaml's
 * `_offer_card` -- see FlightCardWidget.tsx's FlightBaggageOption /
 * FlightConditionOption types) instead of the hardcoded FARE_TIERS /
 * RETURN_FARE_TIERS above, which are no longer used by real bookings --
 * showing a fake $1700-range mock tier next to a real, differently-priced
 * sibling leg was worse than an honest single card, so this now always
 * builds from whatever the itinerary actually carries, treating "the field
 * is missing" the same as "the field is an empty array": unknown.
 *
 * - Arrays absent or empty -> exactly ONE card, everything about
 *   baggage/changes/refunds labelled "not available"/"not stated" rather
 *   than guessed. Missing/empty means UNKNOWN, never "not included" (the
 *   same rule the playbook itself enforces).
 * - Arrays populated -> every COMBINATION of the priced baggage add-ons
 *   becomes its own card -- nothing added, one add-on, the other add-on,
 *   both -- 2^N cards for N distinct purchasable types, each priced at
 *   basePrice + the sum of exactly that combination's own add-on prices,
 *   sorted cheapest first. A bag add-on is anything NOT `scope:
 *   "included"` (rather than requiring the literal string `"available"`)
 *   so a gateway using a different label for the same concept still
 *   produces real cards instead of silently collapsing to one. A change
 *   or refund that is allowed AND carries a `penalty_amount` is also an
 *   add-on, priced at that fee (a product decision -- strictly the fee is
 *   what exercising the right costs, not a purchase price); allowed with
 *   no fee, not allowed, or unstated stay fixed on every card.
 */
function buildApiFareTiers(
    itinerary: FlightCardPayload['itineraries'][0],
    fallbackBrandName: string
): FareTier[] {
    const baggageOptions = itinerary.baggage_options;
    const conditionsOptions = itinerary.conditions_options;
    const firstSeg = itinerary.segments[0];
    const lastSeg = itinerary.segments[itinerary.segments.length - 1];
    const legLabel = `${itinerary.direction || 'leg'} ${firstSeg?.departure.iata || '?'} -> ${lastSeg?.arrival.iata || '?'}`;
    if (process.env.NODE_ENV !== 'production') {
        // Temporary: helps confirm what a real gateway response looks like
        // when checking whether a leg is landing on the honest "unknown"
        // card versus real priced combinations.
        console.log('[buildApiFareTiers] raw itinerary fare data', {
            leg: legLabel,
            fare_brand: itinerary.fare_brand,
            baggage_known: itinerary.baggage_known,
            baggage_options: baggageOptions,
            conditions_known: itinerary.conditions_known,
            conditions_options: conditionsOptions,
        });
    }

    const bags = baggageOptions || [];
    const conditions = conditionsOptions || [];
    const baggageKnown = itinerary.baggage_known ?? bags.length > 0;
    const brandName = itinerary.fare_brand || fallbackBrandName;

    const includedCarryOn = bags.find((b) => b.scope === 'included' && b.type === 'carry_on');
    const includedChecked = bags.find((b) => b.scope === 'included' && b.type === 'checked');
    /* Duffel repeats a term at offer level and again per slice; the
       offer-level entry is the whole-trip statement, so prefer it. */
    const pickCond = (type: string) => {
        const all = conditions.filter((c) => c.type === type);
        return all.find((c) => c.scope === 'offer') || all[0];
    };
    const changeCond = pickCond('change');
    const refundCond = pickCond('refund');

    /* A change/refund that the fare allows AND puts a fee on is treated as
       an add-on priced at that fee -- selecting it on a card adds the fee
       to that card's total, so the traveller sees what each combination
       costs. (Strictly the fee is what exercising the right costs, not a
       purchase price; pricing the card by it is a product decision.) One
       that is allowed with no fee is simply included on every card, one
       that is not allowed shows as unavailable, and an unstated one is
       shown as unknown -- none of those change the price. */
    const conditionFeature = (
        cond: typeof changeCond,
        allowLabel: string,
        denyLabel: string,
        unknownLabel: string
    ): FareTierFeature => {
        if (!cond || cond.allowed === null || cond.allowed === undefined) {
            return { included: false, label: unknownLabel };
        }
        if (cond.allowed) return { included: true, label: allowLabel };
        return { included: false, label: denyLabel, info: 'The fare states this is not allowed.' };
    };
    const condFee = (cond: typeof changeCond) => (cond && cond.allowed === true ? Number(cond.penalty_amount) || 0 : 0);
    const changeIsAddOn = condFee(changeCond) > 0;
    const refundIsAddOn = condFee(refundCond) > 0;
    const conditionCard = (
        cond: typeof changeCond,
        isAddOn: boolean,
        active: boolean,
        allowLabel: string,
        denyLabel: string,
        unknownLabel: string
    ): FareTierFeature =>
        isAddOn
            ? { included: active, label: active ? allowLabel : denyLabel }
            : conditionFeature(cond, allowLabel, denyLabel, unknownLabel);

    const bagFeature = (
        included: typeof includedCarryOn,
        addOn: FlightBaggageOption | undefined,
        active: boolean,
        label: string
    ): FareTierFeature => {
        /* A selected add-on wins over what's already included: when a fare
           includes 1 checked bag and offers a priced "2 bags" option, the
           card that adds it must show 2, not repeat the included 1. */
        if (active && addOn) {
            return {
                included: true,
                label: `${label} — ${addOn.quantity} pc`,
                detail: addOn.amount ? `+${addOn.amount} ${addOn.currency || ''}`.trim() : undefined,
            };
        }
        if (included) return { included: true, label: `${label} — ${included.quantity} pc` };
        return { included: false, label: baggageKnown ? `No ${label.toLowerCase()}` : `${label} info not available` };
    };

    /* One representative entry per purchasable type, not one per raw array
       row: Duffel can list the same add-on more than once (e.g. once per
       segment of a connection, or once per passenger), and summing every
       row would charge a connection's second leg's bag fee on top of the
       first for what is really one "add a checked bag" choice. The
       cheapest entry per type is the conservative pick when duplicates
       exist. */
    const cheapestByType = new Map<string, FlightBaggageOption>();
    for (const opt of bags) {
        /* "included" is the one scope value documented consistently
           everywhere; anything else is treated as a priced add-on rather
           than requiring the literal string "available", so a gateway
           using a slightly different label still produces real cards. */
        if (opt.scope === 'included') continue;
        const amount = Number(opt.amount) || 0;
        const existing = cheapestByType.get(opt.type);
        if (!existing || amount < (Number(existing.amount) || 0)) cheapestByType.set(opt.type, opt);
    }
    const typeLabel = (type: string) => (type === 'carry_on' ? 'carry-on bag' : type === 'checked' ? 'checked bag' : type);

    /* Every add-on dimension a card can switch on: priced bags, then a
       priced change, then a priced refund. */
    type AddOn = { key: string; label: string; price: number };
    const addOns: AddOn[] = [
        ...Array.from(cheapestByType.entries()).map(([type, opt]) => ({
            key: `bag:${type}`,
            label: typeLabel(type),
            price: Number(opt.amount) || 0,
        })),
        ...(changeIsAddOn ? [{ key: 'change', label: 'changes', price: condFee(changeCond) }] : []),
        ...(refundIsAddOn ? [{ key: 'refund', label: 'refund', price: condFee(refundCond) }] : []),
    ];

    const tiers: FareTier[] = [];
    const comboCount = 1 << addOns.length; // 2^N combinations (1 when N = 0)
    for (let mask = 0; mask < comboCount; mask += 1) {
        let priceAdd = 0;
        const active = new Set<string>();
        const addedLabels: string[] = [];
        addOns.forEach((addOn, i) => {
            if (!(mask & (1 << i))) return;
            priceAdd += addOn.price;
            active.add(addOn.key);
            addedLabels.push(addOn.label);
        });
        tiers.push({
            name: addedLabels.length ? `${brandName} + ${addedLabels.join(' & ')}` : brandName,
            priceAdd,
            features: [
                PERSONAL_ITEM_FEATURE,
                bagFeature(includedCarryOn, cheapestByType.get('carry_on'), active.has('bag:carry_on'), 'Carry-on bag'),
                bagFeature(includedChecked, cheapestByType.get('checked'), active.has('bag:checked'), 'Checked bag'),
                conditionCard(changeCond, changeIsAddOn, active.has('change'), 'Changes allowed', 'No changes', 'Change terms not stated'),
                conditionCard(refundCond, refundIsAddOn, active.has('refund'), 'Refundable', 'No refunds', 'Refund terms not stated'),
            ],
        });
    }
    tiers.sort((a, b) => a.priceAdd - b.priceAdd);
    if (process.env.NODE_ENV !== 'production') {
        console.log('[buildApiFareTiers] built', tiers.length, 'card(s) for', legLabel, '- add-ons:', addOns.map((a) => a.key));
    }
    return tiers;
}

type TicketFilter = 'exchange' | 'refund';

export type FlightDetailsResult = {
    fareTier: number;
    /** Only present for a round trip -- the return leg's own selected tier. */
    returnFareTier?: number;
    bagCount: number;
    ticketFilters: TicketFilter[];
};

type FlightDetailsStepProps = {
    booking: FlightCardPayload;
    onContinue: (result: FlightDetailsResult) => void;
};

/* 24-hour and airport-local: the booking timeline is dense, and a
   departure time belongs to the airport rather than to whoever happens to
   be reading the page. */
const formatTime = formatTime24;

const formatDate = formatDateShort;

const isDifferentDay = isDifferentAirportDay;

type SegmentEndpoint = { iata: string; city?: string; airport_name?: string };

/* Prefers the gateway's own city/airport_name when the payload carries
   them; AIRPORT_CITIES/AIRPORT_SHORT stay as the fallback for whichever
   segment endpoint the gateway doesn't state one for. */
function getCity(endpoint: SegmentEndpoint): string {
    return endpoint.city || AIRPORT_CITIES[endpoint.iata.toUpperCase()] || endpoint.iata;
}

/* "Heathrow, LHR" rather than "London Heathrow Airport" — the full name
   wrapped onto three lines in the timeline column and repeated the city
   already shown directly above it. */
function getAirportName(endpoint: SegmentEndpoint): string {
    if (endpoint.airport_name) return endpoint.airport_name;
    const code = endpoint.iata.toUpperCase();
    const short = AIRPORT_SHORT[code];
    return short ? `${short}, ${code}` : code;
}

// Icons as inline SVGs
function PlaneIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
        </svg>
    );
}

function ShieldIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
        </svg>
    );
}

function BagIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
    );
}

function CheckCircleIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="m9 12 2 2 4-4" />
        </svg>
    );
}

function InfoIcon({ className, title }: { className?: string; title?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label={title}>
            {title ? <title>{title}</title> : null}
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
        </svg>
    );
}

function ArrowRightIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
        </svg>
    );
}

/* A bare cross for the excluded rows -- ringing it gives an absence the
   same visual weight as an inclusion. */
function XIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m18 6-12 12" />
            <path d="m6 6 12 12" />
        </svg>
    );
}

function CloudIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.3-2A5 5 0 0 0 6 19h11.5z" />
        </svg>
    );
}

/* Duffel-style facts about the fare -- shown honestly as "no data" when the
   payload doesn't carry them rather than guessing a plausible value. Bags
   lead the list per the reference; "hold space" is Duffel-only and dropped. */
function FlightFactRow({ icon, label, muted = true }: { icon: React.ReactNode; label: string; muted?: boolean }) {
    return (
        <div className={`${styles.factRow} ${muted ? styles.factRowMuted : ''}`}>
            <span className={styles.factIcon}>{icon}</span>
            <span>{label}</span>
        </div>
    );
}

/* One fare-tier row -- used for both the outbound and (once booked) the
   return leg, each with its own tier list and its own select/book state.
   Selecting a card just previews it (FlightLegCard's fact list follows
   along live); "Book" is the deliberate, separate action that locks the
   choice in and -- for the outbound row -- reveals the return row. */
function FareTierSlider({
    tiers,
    basePrice,
    currencySymbol,
    selectedIdx,
    onSelect,
    booked,
    onBook,
}: {
    tiers: FareTier[];
    basePrice: number;
    currencySymbol: string;
    selectedIdx: number;
    onSelect: (idx: number) => void;
    booked: boolean;
    onBook: () => void;
}) {
    return (
        <Slider draggable showArrows={false}>
            {tiers.map((tier, idx) => {
                const tierPrice = (basePrice + tier.priceAdd).toFixed(0);
                const isSelected = selectedIdx === idx;
                const isBooked = booked && isSelected;
                const isLockedOut = booked && !isSelected;
                return (
                    <div
                        key={idx}
                        className={`${styles.fareCard} ${isSelected ? styles.fareCardSelected : ''} ${isLockedOut ? styles.fareCardLocked : ''}`}
                        onClick={() => { if (!booked) onSelect(idx); }}
                        role="button"
                        tabIndex={booked ? -1 : 0}
                        aria-disabled={isLockedOut}
                        onKeyDown={(e) => { if (!booked && (e.key === 'Enter' || e.key === ' ')) onSelect(idx); }}
                    >
                        <img
                            src={FARE_IMAGES[idx % FARE_IMAGES.length]}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className={styles.fareImage}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <div className={styles.fareBody}>
                            <div className={styles.fareHeader}>
                                <span className={styles.fareName}>{tier.name}</span>
                                {isBooked ? (
                                    <span className={styles.fareBookedBadge}>
                                        <CheckCircleIcon className={styles.fareBookedIcon} /> Booked
                                    </span>
                                ) : isSelected ? (
                                    <span className={styles.fareSelectedBadge}>Selected</span>
                                ) : (
                                    <button
                                        type="button"
                                        className={styles.fareChooseBtn}
                                        disabled={booked}
                                        onClick={(e) => { e.stopPropagation(); onSelect(idx); }}
                                    >
                                        Choose
                                    </button>
                                )}
                            </div>
                            <div className={styles.farePrice}>
                                {tierPrice} {currencySymbol}
                            </div>
                            <div className={styles.fareFeatures}>
                                {tier.features.map((feat, fIdx) => (
                                    <div
                                        key={fIdx}
                                        className={`${styles.fareFeatureRow} ${feat.included ? styles.fareFeatureIncluded : styles.fareFeatureExcluded}`}
                                    >
                                        {feat.included ? (
                                            <CheckCircleIcon className={`${styles.fareFeatureIcon} ${styles.fareFeatureIconGreen}`} />
                                        ) : (
                                            <XIcon className={`${styles.fareFeatureIcon} ${styles.fareFeatureIconMuted}`} />
                                        )}
                                        <span className={styles.fareFeatureText}>
                                            <span className={styles.fareFeatureLabel}>
                                                {feat.label}
                                                {"info" in feat && (feat as { info?: string }).info ? (
                                                    <InfoIcon
                                                        className={styles.fareInfoIcon}
                                                        title={(feat as { info?: string }).info}
                                                    />
                                                ) : null}
                                            </span>
                                            {"detail" in feat && (feat as { detail?: string }).detail ? (
                                                <span className={styles.fareFeatureDetail}>
                                                    {(feat as { detail?: string }).detail}
                                                </span>
                                            ) : null}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.fareNote}>
                                Total number of suitcases and bags for all passengers
                            </div>
                            {isSelected && !booked && (
                                <button
                                    type="button"
                                    className={styles.fareBookBtn}
                                    onClick={(e) => { e.stopPropagation(); onBook(); }}
                                >
                                    Book this fare
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </Slider>
    );
}

function FlightLegCard({
    itinerary,
    label,
    activeTier,
    selectedPrice,
    currency,
}: {
    itinerary: FlightCardPayload['itineraries'][0];
    label: string;
    /* Whichever fare tier is currently in play for this leg -- the one the
       traveller has selected while still browsing, or the one they've
       booked. The fact list below always mirrors this fare's own feature
       data, live, so it updates as the selection changes and freezes the
       moment the caller stops passing a different tier in (see the
       booked/selected handling in FlightDetailsStep). */
    activeTier: FareTier;
    selectedPrice: number;
    currency: string;
}) {
    const segments = itinerary.segments;
    const firstSeg = segments[0];
    const lastSeg = segments[segments.length - 1];
    const originCity = getCity(firstSeg.departure);
    const destCity = getCity(lastSeg.arrival);
    const carrierCode = firstSeg.carrier || '';
    /* carrier_name/logo come from the gateway when it states them;
       airlineName()/AirlineLogo's own Duffel-code URL are the fallback for
       whichever carrier it doesn't. */
    const carrierDisplayName = firstSeg.carrier_name || airlineName(carrierCode);
    const carrierLogoSrc = firstSeg.airline?.logo_lockup_url || firstSeg.airline?.logo_symbol_url;
    /* This line only appears when a leg is genuinely flown by someone other
       than the marketing airline -- rather than telling the traveller
       "operated by" itself. */
    const operatingCode = firstSeg.operating_carrier || '';
    const operatedBy =
        operatingCode && operatingCode.toUpperCase() !== carrierCode.toUpperCase()
            ? (firstSeg.operating_carrier_name || airlineName(operatingCode))
            : '';

    const totalDuration = itinerary.duration || segments.reduce((acc, s) => {
        // Simple fallback: sum segment durations text
        return acc ? `${acc}` : s.duration;
    }, '');

    /* Stops are a real, always-available fact -- one fewer than the segment
       count for this leg -- unlike the fare facts below, which the payload
       never carries yet. */
    const stopCount = segments.length - 1;
    const stopAirports = segments.slice(1).map((s) => s.departure.iata).join(', ');

    /* Every FARE_TIERS/RETURN_FARE_TIERS entry lists its features in this
       fixed order: personal item, carry-on, checked bag, changes, refunds.
       CO2 has no fare-tier equivalent, so it stays a flat "no data" row. */
    const [, carryOn, checkedBag, changes, refunds] = activeTier.features;

    return (
        <div className={styles.flightCard}>
            <div className={styles.routeHead}>
                <p className={styles.routeString}>{originCity} — {destCity}</p>
                <p className={styles.totalDuration}>{formatDuration(totalDuration)} total</p>
                <p className={styles.legPrice}>{formatMoney(selectedPrice, currency)}</p>
            </div>

            <div className={styles.airlineRow}>
                <AirlineLogo code={carrierCode} name={carrierCode} src={carrierLogoSrc} size="sm" />
                <div className={styles.airlineInfo}>
                    <div className={styles.airlineName}>{carrierDisplayName}</div>
                    {operatedBy && (
                        <div className={styles.airlineSub}>Operated by {operatedBy}</div>
                    )}
                </div>
            </div>

            <div className={styles.inFlight}>
                {formatDuration(totalDuration)} in flight
                {stopCount > 0 && (
                    <span className={styles.stopsNote}>
                        {' '}· {stopCount} stop{stopCount > 1 ? 's' : ''} ({stopAirports})
                    </span>
                )}
            </div>

            <div className={styles.timeline}>
                <div className={styles.timelineLine}>
                    <div className={styles.timelineDot} />
                    <div className={styles.timelineDash} />
                    <div className={styles.timelineDot} />
                </div>
                <div className={styles.timelinePoints}>
                    <div className={styles.timelinePoint}>
                        <div>
                            <div className={styles.timelineTime}>{formatTime(firstSeg.departure.at)}</div>
                            <div className={styles.timelineDate}>{formatDate(firstSeg.departure.at)}</div>
                        </div>
                        <div>
                            <div className={styles.timelineCity}>{originCity}</div>
                            <div className={styles.timelineAirport}>{getAirportName(firstSeg.departure)}</div>
                        </div>
                    </div>
                    <div className={styles.timelinePoint}>
                        <div>
                            <div className={styles.timelineTime}>{formatTime(lastSeg.arrival.at)}</div>
                            <div className={isDifferentDay(firstSeg.departure.at, lastSeg.arrival.at) ? styles.timelineDateAmber : styles.timelineDate}>
                                {formatDate(lastSeg.arrival.at)}
                            </div>
                        </div>
                        <div>
                            <div className={styles.timelineCity}>{destCity}</div>
                            <div className={styles.timelineAirport}>{getAirportName(lastSeg.arrival)}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.factList}>
                <FlightFactRow
                    icon={carryOn.included
                        ? <CheckCircleIcon className={`${styles.factIconSvg} ${styles.factIconGreen}`} />
                        : <XIcon className={styles.factIconSvg} />}
                    label={carryOn.label}
                    muted={!carryOn.included}
                />
                <FlightFactRow
                    icon={checkedBag.included
                        ? <CheckCircleIcon className={`${styles.factIconSvg} ${styles.factIconGreen}`} />
                        : <XIcon className={styles.factIconSvg} />}
                    label={checkedBag.label}
                    muted={!checkedBag.included}
                />
                <FlightFactRow
                    icon={changes.included
                        ? <CheckCircleIcon className={`${styles.factIconSvg} ${styles.factIconGreen}`} />
                        : <XIcon className={styles.factIconSvg} />}
                    label={changes.label}
                    muted={!changes.included}
                />
                <FlightFactRow
                    icon={refunds.included
                        ? <CheckCircleIcon className={`${styles.factIconSvg} ${styles.factIconGreen}`} />
                        : <XIcon className={styles.factIconSvg} />}
                    label={refunds.label}
                    muted={!refunds.included}
                />
                <FlightFactRow icon={<CloudIcon className={styles.factIconSvg} />} label="No data on CO2" />
            </div>
        </div>
    );
}

export function FlightDetailsStep({ booking, onContinue }: FlightDetailsStepProps) {
    const [outboundFareTier, setOutboundFareTier] = useState(0);
    const [outboundBooked, setOutboundBooked] = useState(false);
    const [returnFareTier, setReturnFareTier] = useState(0);
    const [returnBooked, setReturnBooked] = useState(false);
    const [bagCount, setBagCount] = useState(0);
    const [ticketFilters, setTicketFilters] = useState<TicketFilter[]>([]);

    const basePrice = parseFloat(booking.price.total) || 0;
    const currency = booking.price.currency || 'USD';
    /* The fare table prints the amount and then the symbol — "1366 $". */
    const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };
    const currencySymbol = symbols[currency.toUpperCase()] || currency;
    const itineraries = booking.itineraries || [];
    const direction = String(booking.direction || '').toLowerCase();
    const hasReturn = itineraries.length > 1 && !['one-way', 'one_way'].includes(direction);
    const numberOfBookableSeats = (booking as any).numberOfBookableSeats;

    /* Real gateway data when the leg's itinerary carries it (see
       buildApiFareTiers); the hardcoded FARE_TIERS/RETURN_FARE_TIERS stay
       as the fallback for a booking with no baggage_options/
       conditions_options at all (an older/mock payload). */
    const outboundTiers = (itineraries[0] && buildApiFareTiers(itineraries[0], 'This fare')) || FARE_TIERS;
    const returnTiers = hasReturn
        ? (itineraries[1] && buildApiFareTiers(itineraries[1], 'This fare')) || RETURN_FARE_TIERS
        : [];

    /* Each leg prices independently -- `booking.price` is the combined
       round-trip total once outbound+return are merged into one booking
       (see Chat.tsx's handleFlightAction), so the outbound card's price
       must come from the outbound leg's OWN price, and the return card's
       from the return leg's own, not from that shared total. Falls back to
       `basePrice` for a one-way booking or an older payload that predates
       the per-leg stamp. */
    const outboundBasePrice = parseFloat(itineraries[0]?.price?.total ?? '') || basePrice;
    const returnBasePrice = hasReturn ? (parseFloat(itineraries[1]?.price?.total ?? '') || 0) : 0;

    const outboundSelectedPrice = outboundBasePrice + outboundTiers[outboundFareTier].priceAdd;
    const returnSelectedPrice = hasReturn ? returnBasePrice + returnTiers[returnFareTier].priceAdd : 0;

    const toggleFilter = (filter: TicketFilter) => {
        setTicketFilters((prev) =>
            prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]
        );
    };

    const handleContinue = () => {
        onContinue({
            fareTier: outboundFareTier,
            returnFareTier: hasReturn ? returnFareTier : undefined,
            bagCount,
            ticketFilters,
        });
    };

    return (
        <section className={styles.root}>
            {/* Header */}
            <div className={styles.headerRow}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerIconWrap}>
                        <PlaneIcon className={styles.headerIcon} />
                    </div>
                    <span className={styles.headerTitle}>Flight details</span>
                    <span className={styles.headerStep}>Step 1 of 4</span>
                </div>
                <div className={styles.headerRight}>
                    <ShieldIcon className={styles.shieldIcon} />
                    <span>Secure booking</span>
                </div>
            </div>

            {/* Flight cards -- each leg's fact list mirrors whichever fare is
                currently in play for it (selected-but-browsing, or booked). */}
            <div className={`${styles.flightCardsGrid} ${itineraries.length === 1 ? styles.singleCard : ''}`}>
                {itineraries.map((itin, idx) => (
                    <FlightLegCard
                        key={idx}
                        itinerary={itin}
                        label={idx === 0 ? 'Outbound' : 'Return'}
                        activeTier={idx === 0 ? outboundTiers[outboundFareTier] : returnTiers[returnFareTier]}
                        selectedPrice={idx === 0 ? outboundSelectedPrice : returnSelectedPrice}
                        currency={currency}
                    />
                ))}
            </div>

            {/* Baggage row */}
            {/*<div className={styles.baggageRow}>*/}
            {/*    <div className={styles.baggageLeft}>*/}
            {/*        <BagIcon className={styles.baggageIcon} />*/}
            {/*        <span>How many bags will you check?</span>*/}
            {/*    </div>*/}
            {/*    <div className={styles.baggageControls}>*/}
            {/*        <button*/}
            {/*            type="button"*/}
            {/*            className={styles.baggageBtn}*/}
            {/*            onClick={() => setBagCount((v) => Math.max(0, v - 1))}*/}
            {/*            aria-label="Decrease bag count"*/}
            {/*        >*/}
            {/*            −*/}
            {/*        </button>*/}
            {/*        <span className={styles.baggageCount}>from {bagCount} pcs</span>*/}
            {/*        <button*/}
            {/*            type="button"*/}
            {/*            className={styles.baggageBtn}*/}
            {/*            onClick={() => setBagCount((v) => v + 1)}*/}
            {/*            aria-label="Increase bag count"*/}
            {/*        >*/}
            {/*            +*/}
            {/*        </button>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* Ticket type filter */}
            <div className={styles.ticketFilter}>
                <button
                    type="button"
                    className={`${styles.ticketPill} ${ticketFilters.includes('exchange') ? styles.ticketPillActive : ''}`}
                    onClick={() => toggleFilter('exchange')}
                >
                    With exchange
                </button>
                <button
                    type="button"
                    className={`${styles.ticketPill} ${ticketFilters.includes('refund') ? styles.ticketPillActive : ''}`}
                    onClick={() => toggleFilter('refund')}
                >
                    With refund
                </button>
            </div>

            {/* Seats warning */}
            {typeof numberOfBookableSeats === 'number' && numberOfBookableSeats <= 5 && (
                <div className={styles.seatsWarning}>Only {numberOfBookableSeats} seats left</div>
            )}

            {/* Fare tier cards -- the gateway can return more than 3, so this
                is a drag-scroll row rather than a fixed 3-column grid.
                Booking the outbound fare reveals the return row below it. */}
            {hasReturn && <h3 className={styles.fareSectionTitle}>Outbound fare</h3>}
            <FareTierSlider
                tiers={outboundTiers}
                basePrice={outboundBasePrice}
                currencySymbol={currencySymbol}
                selectedIdx={outboundFareTier}
                onSelect={setOutboundFareTier}
                booked={outboundBooked}
                onBook={() => setOutboundBooked(true)}
            />

            {hasReturn && outboundBooked && (
                <>
                    <h3 className={styles.fareSectionTitle}>Return fare</h3>
                    <FareTierSlider
                        tiers={returnTiers}
                        basePrice={returnBasePrice}
                        currencySymbol={currencySymbol}
                        selectedIdx={returnFareTier}
                        onSelect={setReturnFareTier}
                        booked={returnBooked}
                        onBook={() => setReturnBooked(true)}
                    />
                </>
            )}

            {/* Bottom bar */}
            <div className={styles.bottomBar}>
                <div className={styles.bottomLeft}>
                    <ShieldIcon className={styles.bottomShield} />
                    <div className={styles.bottomTextGroup}>
                        <span className={styles.bottomTitle}>No hidden fees</span>
                        <span className={styles.bottomSub}>All taxes and fees included</span>
                    </div>
                </div>
                <button type="button" className={styles.continueBtn} onClick={handleContinue}>
                    <span>Continue</span>
                    <ArrowRightIcon className={styles.continueArrow} />
                </button>
            </div>
        </section>
    );
}
